import { InputFactory } from "factories/InputFactory";
import { SubsidiaryFormField, SubsidiaryFormValues } from "features/new-actor/types/optim/subsidiaryFormValues";
import { NsKey } from "enums/NsKey";
import { Formik, FormikProps } from "formik";
import { useDialog } from "hooks/useDialog";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { usePanel } from "hooks/usePanel";
import { FC, ReactNode, useEffect, useMemo, useRef } from "react";
import { subsidiaryInputsConfig } from "./subsidiaryInputsConfig";
import { buildSubsidiaryValidationSchema } from "./subsidiaryValidationRules";

// Identity of an attached row comes from the existing legal entity: only the figures are editable.
const ATTACHED_READ_ONLY_FIELDS: SubsidiaryFormField[] = ['companyName', 'registrationNumber'];

interface SubsidiaryFormProps {
  handleSubmit: (subsidiary: SubsidiaryFormValues) => void | Promise<void>
  /** Row to edit, or an attach draft prefilled from the search bar. */
  pendingSubsidiary?: SubsidiaryFormValues
  parentRegistrationNumber?: string | null
  subsidiaries?: SubsidiaryFormValues[]
  /**
   * Rendered in the side panel instead of the dialog: the panel carries the actions, and closing
   * it is left to `handleSubmit`, which saves through the API and may fail.
   */
  inPanel?: boolean
  /**
   * Arrangement of the fields, given by the caller. The fields themselves are this form's: the
   * caller only places them (a field it leaves out keeps its value in the Formik state, so the
   * validation and the saved command do not change). Default: the dialog grid.
   */
  renderLayout?: (renderField: (fieldName: SubsidiaryFormField) => ReactNode) => ReactNode
}

const emptySubsidiary = (): SubsidiaryFormValues => ({
  id: '',
  companyName: '',
  registrationNumber: '',
  revenue: '',
  employeesCount: '',
  fiscalYearEnd: '',
  legalPersonId: null
})

export const SubsidiaryForm: FC<SubsidiaryFormProps> = ({
  handleSubmit,
  pendingSubsidiary,
  parentRegistrationNumber,
  subsidiaries,
  inPanel = false,
  renderLayout
}) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm])
  const { setDialogActions, closeDialog } = useDialog()
  const { setPanelActions } = usePanel()
  const formikRef = useRef<FormikProps<SubsidiaryFormValues>>(null)

  const initialValues = useMemo(() => pendingSubsidiary ?? emptySubsidiary(), [pendingSubsidiary])
  const isAttached = Boolean(initialValues.legalPersonId)

  const validationSchema = useMemo(() => buildSubsidiaryValidationSchema({
    parentRegistrationNumber,
    subsidiaries,
    currentId: initialValues.id || undefined
  }, t), [parentRegistrationNumber, subsidiaries, initialValues.id, t])

  useEffect(() => {
    const actions = {
      closeLabel: "COMMON.CANCEL",
      submitLabel: "COMMON.VALIDATE",
      // Formik validates first: the overlay only closes from onSubmit, once the row is valid.
      onSubmit: () => formikRef.current?.submitForm()
    }
    if (inPanel) {
      setPanelActions(actions)
      return
    }
    setDialogActions(actions)
  }, [inPanel, setPanelActions, setDialogActions])

  const onSubmit = async (values: SubsidiaryFormValues) => {
    await handleSubmit(values)
    // In the panel the row is saved through the API: a failure must leave the form open.
    if (!inPanel) {
      closeDialog()
    }
  }

  const renderField = (fieldName: SubsidiaryFormField) => (
    <InputFactory
      config={subsidiaryInputsConfig}
      fieldName={fieldName}
      disabledFields={isAttached ? ATTACHED_READ_ONLY_FIELDS : undefined}
    />
  )

  // No <form> element: the dialog may sit inside the legal entity form in the React tree,
  // and a nested submit event would bubble up to it.
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {renderLayout ? <>{renderLayout(renderField)}</> : (
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">{renderField('companyName')}</div>
          <div className="col-span-3">{renderField('registrationNumber')}</div>
          <div className="col-span-2">{renderField('revenue')}</div>
          <div className="col-span-2">{renderField('fiscalYearEnd')}</div>
          <div className="col-span-2">{renderField('employeesCount')}</div>
        </div>
      )}
    </Formik>
  )
}
